import { Button } from "@/components/flexkit/Button";
import { DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import type { TrackLocation } from "@/hooks/use-segment";
import { useDeleteLLMIntegration } from "@/queries/runner-configuration-queries";
import { formatError } from "@/utils/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import type { LLMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type FC, useState } from "react";

type LLMIntegrationRemoveModalProps = {
    runnerId: string;
    integration: LLMIntegration;
    onClose: () => void;
    onRemoved?: () => void;
    "data-track-location": TrackLocation;
};

export const LLMIntegrationRemoveModal: FC<LLMIntegrationRemoveModalProps> = ({
    runnerId,
    integration,
    onClose,
    onRemoved,
    "data-track-location": dataTrackLocation,
}) => {
    const deleteIntegration = useDeleteLLMIntegration(runnerId);
    const [preconditionError, setPreconditionError] = useState<string | null>(null);

    const handleDelete = async (force = false) => {
        try {
            await deleteIntegration.mutateAsync({ integrationId: integration.id, force });

            toast({
                title: "LLM integration removed",
                description: "The LLM integration has been successfully removed",
            });

            if (onRemoved) {
                onRemoved();
            } else {
                onClose();
            }
        } catch (error) {
            if (error instanceof ConnectError && error.code === Code.FailedPrecondition && !force) {
                setPreconditionError(error.rawMessage);
                return;
            }
            toast({
                title: "Failed to remove LLM integration",
                description: formatError(error),
            });
        }
    };

    return (
        <div className="flex flex-col gap-6" data-track-location={dataTrackLocation}>
            <DialogHeader>
                <DialogTitle>Remove LLM Integration</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4">
                {preconditionError ? (
                    <>
                        <Text className="text-content-negative">{preconditionError}</Text>
                        <Text className="text-sm text-content-secondary">
                            You can force delete this integration, but AI features that depend on it may stop working.
                        </Text>
                    </>
                ) : (
                    <>
                        <Text>
                            Are you sure you want to remove the LLM integration for{" "}
                            <span className="font-medium">{integration.endpoint}</span>?
                        </Text>
                        <Text className="text-sm text-content-secondary">
                            This action cannot be undone. The integration will be permanently removed and AI features
                            using this integration will no longer work.
                        </Text>
                    </>
                )}

                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        data-tracking-id="cancel-remove-llm-integration-llm-integration-remove-modal"
                    >
                        Cancel
                    </Button>
                    {preconditionError ? (
                        <Button
                            type="button"
                            variant="destructive"
                            loading={deleteIntegration.isPending}
                            onClick={() => handleDelete(true)}
                            data-testid="force-remove-llm-integration-button"
                            data-tracking-id="force-remove-llm-integration-llm-integration-remove-modal"
                        >
                            Force Remove
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="destructive"
                            loading={deleteIntegration.isPending}
                            onClick={() => handleDelete(false)}
                            data-testid="confirm-remove-llm-integration-button"
                            data-tracking-id="confirm-remove-llm-integration-llm-integration-remove-modal"
                        >
                            Remove Integration
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
